import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';
// import { useDispatch } from 'react-redux';
import { staticStyles } from './style';

const LeanPay = ({ customerId, depositId, appToken, sdkToken, isSandbox = false }) => {
  // const [emiratesIdUpdated, setEmiratesIdUpdated] = useState(null);
  const consentIdRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [resultMessage, setResultMessage] = useState({ message: '', isSuccess: false, isVisible: false });
  const [paymentResultMessage, setPaymentResultMessage] = useState({ message: '', isSuccess: false, isVisible: false });
  const [showEmiratesIdModal, setShowEmiratesIdModal] = useState(false);
  const [emiratesId, setEmiratesId] = useState('');
  const [emiratesIdError, setEmiratesIdError] = useState('');
  const emiratesIdSuccessCallback = useRef(null);

  // Get URL parameter helper function
  const getUrlParameter = name => {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get(name) || '';
  };

  // Initialize configuration
  const APP_TOKEN = appToken || '';
  const SDK_TOKEN = sdkToken || '';
  const CUSTOMER_ID = customerId || '';
  const DEPOSIT_ID = depositId || 0;
  // const paymentDestinationId = getUrlParameter('payment_destination_id') || '266733cc-6f46-4e87-af7e-9bb46b99601d';

  // Show result message
  const showResultMessage = (message, isSuccess) => {
    setResultMessage({ message, isSuccess, isVisible: true });
    setTimeout(() => {
      setResultMessage(prev => ({ ...prev, isVisible: false }));
    }, 10000);
  };

  // Show payment result message
  const showPaymentResultMessage = (message, isSuccess) => {
    setPaymentResultMessage({ message, isSuccess, isVisible: true });
    setTimeout(() => {
      setPaymentResultMessage(prev => ({ ...prev, isVisible: false }));
    }, 5000);
  };

  // Check Emirates ID status
  const checkEmiratesIdStatus = onSuccessCallback => {
    fetch(`/api/payments/leantech/customer/update-id/?customer_id=${CUSTOMER_ID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          // setEmiratesIdUpdated(data.customer_id_updated || false);
          const consentStatus = data.consent_status || '';
          // const consentAuthorizedFlag = data.consent_authorized || false;

          // Check consent status - only block if status exists but is not AUTHORIZED
          // Empty consentStatus means no consent yet, which is OK (will create new one)
          if (consentStatus && consentStatus !== 'AUTHORIZED') {
            showResultMessage(
              `Consent status is "${consentStatus}". Please wait for authorization to complete before proceeding.`,
              false
            );
            return;
          }

          // If not updated, show modal
          if (!data.customer_id_updated) {
            emiratesIdSuccessCallback.current = onSuccessCallback;
            setShowEmiratesIdModal(true);
          } else if (onSuccessCallback && typeof onSuccessCallback === 'function') {
            onSuccessCallback();
          }
        } else {
          console.warn('Failed to check Emirates ID status:', data.error);
          if (onSuccessCallback && typeof onSuccessCallback === 'function') {
            onSuccessCallback();
          }
        }
      })
      .catch(error => {
        console.error('Error checking Emirates ID status:', error);
        if (onSuccessCallback && typeof onSuccessCallback === 'function') {
          onSuccessCallback();
        }
      });
  };

  // Submit Emirates ID
  const submitEmiratesId = () => {
    if (!emiratesId || emiratesId.length < 15) {
      setEmiratesIdError('Please enter a valid Emirates ID');
      return;
    }

    setEmiratesIdError('');
    setLoading(true);

    fetch('/api/payments/leantech/customer/update-id/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: CUSTOMER_ID,
        id: emiratesId,
      }),
    })
      .then(response => response.json())
      .then(data => {
        setLoading(false);
        if (data.success) {
          // setEmiratesIdUpdated(true);
          setShowEmiratesIdModal(false);
          showResultMessage('Emirates ID updated successfully.', true);

          setTimeout(() => {
            if (emiratesIdSuccessCallback.current && typeof emiratesIdSuccessCallback.current === 'function') {
              emiratesIdSuccessCallback.current();
              emiratesIdSuccessCallback.current = null;
            }
          }, 1000);
        } else {
          setEmiratesIdError(data.error || 'Failed to update Emirates ID');
        }
      })
      .catch(error => {
        setLoading(false);
        console.error('Error updating Emirates ID:', error);
        setEmiratesIdError('Network error. Please try again.');
      });
  };

  // Create consent
  const createConsent = onSuccessCallback => {
    showResultMessage('Creating consent...', true);

    fetch('/api/payments/leantech/consent/create/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: CUSTOMER_ID,
        deposit_id: DEPOSIT_ID,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success && data.consent_id) {
          // setConsentId(data.consent_id); // This line is unnecessary if you are using the ref
          consentIdRef.current = data.consent_id;
          console.log('Consent created, ID:', data.consent_id);
          showResultMessage('Consent created successfully. Authorizing...', true);

          // Execute callback immediately after setting ref
          if (onSuccessCallback && typeof onSuccessCallback === 'function') {
            setTimeout(() => {
              console.log('Executing authorizeConsent callback, consentIdRef:', consentIdRef.current);
              onSuccessCallback();
            }, 1000);
          }
        } else {
          showResultMessage(`Failed to create consent: ${data.error || 'Unknown error'}`, false);
        }
      })
      .catch(error => {
        console.error('Error creating consent:', error);
        showResultMessage('Network error while creating consent. Please try again.', false);
      });
  };

  // Check consent authorization status
  const checkConsentStatus = callback => {
    fetch(`/api/payments/leantech/customer/update-id/?customer_id=${CUSTOMER_ID}`, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then(response => response.json())
      .then(data => {
        const consentStatus = (data.consent_status || '').toUpperCase();
        if (data.success && (consentStatus === 'AUTHORIZED' || consentStatus === 'AUTHORISED') && data.consent_id) {
          // Consent is authorized, set consent_id for payment
          consentIdRef.current = data.consent_id;
          callback(true);
        } else {
          callback(false);
        }
      })
      .catch(error => {
        console.error('Error checking consent status:', error);
        callback(false);
      });
  };

  // Create payment
  const createPayment = () => {
    const currentConsentId = consentIdRef.current;
    if (!currentConsentId) {
      showResultMessage('Please authorize account access first.', false);
      return;
    }

    showPaymentResultMessage('Creating payment transaction...', true);

    const paymentData = {
      app_token: APP_TOKEN,
      request_id: parseInt(DEPOSIT_ID, 10),
      access_token: SDK_TOKEN,
      customer_id: CUSTOMER_ID,
      consent_id: currentConsentId,
      channel: 'WEB',
    };

    fetch('/api/payments/leantech/payment/intent/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(paymentData),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          showPaymentResultMessage(`Payment created successfully!`, true);
          setTimeout(() => {
            window.location.href = '/client/deposit/';
          }, 2000);
        } else {
          showPaymentResultMessage(`Payment creation failed: ${data.error || 'Unknown error'}`, false);
        }
      })
      .catch(error => {
        showPaymentResultMessage(`Error creating payment: ${error.message}`, false);
      });
  };

  // Handle consent authorization callback
  const handleConsentAuthorization = result => {
    if (result.status !== 'SUCCESS') {
      showResultMessage(`Consent authorization failed: ${result.message}`, false);
      return;
    }

    // setConsentAuthorized(true);
    showResultMessage('Account access authorized successfully! Creating payment...', true);
    createPayment();
  };

  // Perform authorization
  const performAuthorization = () => {
    try {
      const currentConsentId = consentIdRef.current;
      if (!currentConsentId) {
        showResultMessage('No consent ID available for authorization.', false);
        return;
      }

      showResultMessage('Authorizing account access...', true);

      const successRedirectUrl = `${window.location.origin}/wallet/dw/${DEPOSIT_ID}?lean_auth_status=success`;
      const failRedirectUrl = `${window.location.origin}/wallet/dw/${DEPOSIT_ID}?lean_auth_status=fail`;

      const authorizeOptions = {
        app_token: APP_TOKEN,
        consent_id: currentConsentId,
        customer_id: CUSTOMER_ID,
        access_token: SDK_TOKEN,
        sandbox: isSandbox,
        success_redirect_url: successRedirectUrl,
        fail_redirect_url: failRedirectUrl,
        callback: handleConsentAuthorization,
      };

      window.Lean.authorizeConsent(authorizeOptions);
    } catch (error) {
      showResultMessage(`Error: ${error.message}`, false);
    }
  };

  // Authorize consent
  const authorizeConsent = () => {
    const currentConsentId = consentIdRef.current;
    if (!currentConsentId) {
      showResultMessage('No consent ID provided. Please restart the payment process.', false);
      return;
    }

    checkConsentStatus(isAuthorized => {
      if (isAuthorized) {
        showResultMessage('Account access already authorized. Creating payment...', true);
        // setConsentAuthorized(true);
        createPayment();
        return;
      }

      performAuthorization();
    });
  };

  // Handle redirect callback
  const handleRedirectCallback = () => {
    showResultMessage('Processing payment redirect...', true);

    try {
      window.Lean.captureRedirect({
        access_token: SDK_TOKEN,
        sandbox: isSandbox,
        callback: result => {
          if (result.status === 'SUCCESS') {
            showResultMessage('Payment redirect processed successfully!', true);
            setTimeout(() => {
              const parentWindow = window.parent;
              const messageData = {
                command: 'CLOSE_MODAL',
                payload: 'redirect_success',
              };
              const targetOrigin = window.location.origin;
              parentWindow.postMessage(messageData, targetOrigin);
              console.log('Redirect handled, closing modal.');
            }, 2000);
          } else {
            showResultMessage(`Payment redirect failed: ${result.message}`, false);
            setTimeout(() => {
              const parentWindow = window.parent;
              const messageData = {
                command: 'CLOSE_MODAL',
                payload: 'redirect_failed',
              };
              const targetOrigin = window.location.origin;
              parentWindow.postMessage(messageData, targetOrigin);
            }, 3000);
          }
        },
      });
    } catch (error) {
      console.error('Error calling captureRedirect:', error);
      showResultMessage(`Error processing redirect: ${error.message}`, false);
    }
  };

  // Save consent_id to backend after authorization success
  const saveConsentToBackend = (consentId, callback) => {
    fetch('/api/payments/leantech/consent/save/', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        customer_id: CUSTOMER_ID,
        deposit_id: DEPOSIT_ID,
        consent_id: consentId,
      }),
    })
      .then(response => response.json())
      .then(data => {
        if (data.success) {
          console.log('Consent saved to backend successfully');
          if (callback && typeof callback === 'function') {
            callback(true);
          }
        } else {
          console.warn('Failed to save consent to backend:', data.error);
          if (callback && typeof callback === 'function') {
            callback(false);
          }
        }
      })
      .catch(error => {
        console.error('Error saving consent to backend:', error);
        if (callback && typeof callback === 'function') {
          callback(false);
        }
      });
  };

  // Load Lean SDK script
  useEffect(() => {
    const script = document.createElement('script');
    script.src = 'https://cdn.leantech.me/link/loader/prod/ae/latest/lean-link-loader.min.js';
    script.async = true;
    document.head.appendChild(script);

    return () => {
      // Cleanup: remove script when component unmounts
      if (document.head.contains(script)) {
        document.head.removeChild(script);
      }
    };
  }, []);

  // Component mount initialization logic
  useEffect(() => {
    // Check if this is a redirect callback from Lean authorization
    const urlParams = new URLSearchParams(window.location.search);
    const leanAuthStatus = urlParams.get('lean_auth_status');
    const urlConsentId = getUrlParameter('consent_id');

    // Handle Lean authorization callback - save consent_id and continue payment flow
    if (leanAuthStatus === 'success' && urlConsentId) {
      showResultMessage('Authorization successful! Saving consent...', true);
      consentIdRef.current = urlConsentId;

      // Save consent_id to backend first, then continue payment flow
      saveConsentToBackend(urlConsentId, () => {
        showResultMessage('Consent saved! Continuing payment process...', true);
        // Continue with payment flow: checkEmiratesIdStatus -> authorizeConsent -> createPayment
        checkEmiratesIdStatus(authorizeConsent);
      });
      return;
    }

    // Handle failed authorization
    if (leanAuthStatus === 'fail') {
      showResultMessage('Authorization callback failed. Please try again.', false);
      return;
    }

    // Check if this is a payment redirect callback (with status parameter)
    const isRedirectCallback = urlParams.has('status') || window.location.href.includes('status=');

    if (isRedirectCallback) {
      handleRedirectCallback();
      return;
    }

    // New Account-on-file flow: check Emirates ID first, then check if already has authorized consent
    showResultMessage('Preparing payment process...', true);
    checkEmiratesIdStatus(() => {
      // Check if already has an authorized consent before creating a new one
      checkConsentStatus(isAuthorized => {
        if (isAuthorized) {
          // Already has authorized consent, skip createConsent and authorizeConsent
          showResultMessage('Account access already authorized. Creating payment...', true);
          createPayment();
        } else {
          // No authorized consent, create new consent and authorize
          createConsent(authorizeConsent);
        }
      });
    });
  }, []);

  return (
    <div className="LeanPay">
      {/* Emirates ID Modal */}
      {showEmiratesIdModal && (
        <div className="emirates-id-modal">
          <div className="emirates-id-modal-content">
            <div className="emirates-id-modal-title">
              <FormattedMessage id="emiratesIdRequired" defaultmessage="Emirates ID Required" />
            </div>
            <div className="emirates-id-modal-description">
              <FormattedMessage
                id="emiratesIdDescription"
                defaultmessage="Please enter your Emirates ID to continue with the payment process. This information is required for identity verification."
              />
            </div>
            <div className="emirates-id-input-group">
              <label htmlFor="emiratesIdInput">
                <FormattedMessage id="emiratesIdLabel" defaultmessage="Emirates ID *" />
              </label>
              <input
                type="text"
                id="emiratesIdInput"
                value={emiratesId}
                onChange={e => setEmiratesId(e.target.value)}
                placeholder="784-XXXX-XXXXXXX-X"
                maxLength="18"
                disabled={loading}
              />
              {emiratesIdError && <div className="emirates-id-error">{emiratesIdError}</div>}
            </div>
            <div className="emirates-id-modal-buttons">
              <button className="btn-confirm" onClick={submitEmiratesId} disabled={loading}>
                {loading ? (
                  <FormattedMessage id="updating" defaultmessage="Updating..." />
                ) : (
                  <FormattedMessage id="confirm" defaultmessage="Confirm" />
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Result message */}
      {resultMessage.isVisible && (
        <div className={`result-message ${resultMessage.isSuccess ? 'success' : 'error'}`}>{resultMessage.message}</div>
      )}

      {/* Verification message */}
      {/* {verificationMessage.isVisible && (
        <div className={`verification-message ${verificationMessage.isSuccess ? 'success' : 'error'}`}>
          {verificationMessage.message}
        </div>
      )} */}

      {/* Payment result message */}
      {paymentResultMessage.isVisible && (
        <div className={`result-message ${paymentResultMessage.isSuccess ? 'success' : 'error'}`}>
          {paymentResultMessage.message}
        </div>
      )}

      <style jsx>{staticStyles}</style>
    </div>
  );
};

LeanPay.propTypes = {
  customerId: PropTypes.string,
  depositId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  appToken: PropTypes.string,
  sdkToken: PropTypes.string,
  isSandbox: PropTypes.bool,
};

LeanPay.defaultProps = {
  customerId: '',
  depositId: '',
  appToken: '',
  sdkToken: '',
  isSandbox: false,
};

export default LeanPay;
